import { Alert, Box, Button, Divider, Stack, TextField, Typography, styled } from '@mui/material';
import { CenteredFormPage } from 'components/CenteredFormPage';
import { GoogleLogin } from 'components/login/GoogleLogin';
import { MicrosoftLogin } from 'components/login/MicrosoftLogin';
import { PasswordChecklist } from 'components/shared/PasswordChecklist';
import { PasswordField } from 'components/shared/PasswordField';
import { LinkButton } from 'components/shared/LinkButton';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo } from 'react';
import { useRootStore } from 'stores/RootStore';

const CustomAlert = styled(Alert)(({ theme }) => ({
  '&.MuiAlert-standardInfo': {
    backgroundColor: '#4260D314',
    color: '#03186E',
  },
  '& .MuiAlert-icon': {
    color: theme.palette.primary.main,
  },
}));

const RegisterPage = observer(() => {
  const { registerUserStore, routeStore, socialAuthStore } = useRootStore();
  const { navigateByName } = routeStore;
  const { form, message, status, registerUser } = registerUserStore;
  const { message: socialMessage, status: socialStatus, azureLogin } = socialAuthStore;
  const displayMessage = message || socialMessage;
  const googleLoginOptions = useMemo(() => ({ theme: 'outline', logo_alignment: 'center' }), []);
  const passwordField = form.$('password');

  useEffect(() => {
    if (displayMessage) window.scrollTo(0, 0);
  }, [displayMessage]);

  const handleKeyInField = (evt: ReactKeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        registerUser();
      }
    }
  };

  return (
    <CenteredFormPage
      aria-label="signup page"
      belowHeader={
        <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
          <CustomAlert aria-label="register-required-message" severity="info">
            We are enhancing our security measures. Please create an account to access the diligence portal.
          </CustomAlert>
        </Box>
      }
    >
      <form autoComplete="off">
        <Stack spacing={2} sx={{ margin: '32px 0' }}>
          <Typography variant="h5" textAlign="center" style={{ marginBottom: displayMessage ? undefined : '32px' }}>
            <strong>Create an Account</strong>
          </Typography>
          {displayMessage && (
            <Alert
              aria-label="form-error"
              severity="error"
              action={
                status === 'errorEmailExists' || socialStatus === 'errorEmailExists' ? (
                  <Button color="inherit" size="small" onClick={() => navigateByName('login')}>
                    LOG IN
                  </Button>
                ) : undefined
              }
            >
              {displayMessage}
            </Alert>
          )}
          <GoogleLogin options={googleLoginOptions} fullWidth />
          <MicrosoftLogin onClick={() => azureLogin()} fullWidth />
          <Divider sx={{ marginTop: '32px !important', marginBottom: '32px !important' }}>
            <Typography variant="body2" sx={{ color: '#22222261' }}>
              OR
            </Typography>
          </Divider>
          <TextField
            variant="outlined"
            autoComplete="username"
            onKeyDown={handleKeyInField}
            required
            {...form.$('newEmail').bind()}
          />
          <PasswordField
            variant="outlined"
            autoComplete="new-password"
            onKeyDown={handleKeyInField}
            data-testid="password"
            required
            {...passwordField.bind()}
            inputProps={{
              role: 'textbox',
            }}
            helperText={undefined}
          />
          <PasswordChecklist password={passwordField.value} />
          <Typography variant="body1" alignSelf="center" color="text.secondary" sx={{ marginTop: '32px !important' }}>
            By continuing you agree to Bowery's{' '}
            <LinkButton onClick={() => navigateByName('registerTerms')}>Terms of Use.</LinkButton>
          </Typography>
          <Button
            variant="contained"
            disableElevation
            disabled={!form.isValid}
            style={{ height: 56 }}
            onClick={() => registerUser()}
          >
            Create Account
          </Button>
          <Typography variant="body1" alignSelf="center" color="text.secondary" style={{ marginTop: '32px' }}>
            Already have an account? <LinkButton onClick={() => navigateByName('login')}>Log in.</LinkButton>
          </Typography>
          <Typography variant="body1" alignSelf="center" color="text.secondary">
            Need help? <LinkButton onClick={() => navigateByName('registerContact')}>Contact Support.</LinkButton>
          </Typography>
        </Stack>
      </form>
    </CenteredFormPage>
  );
});

export { RegisterPage };
